import React, {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { searchSchools } from '../../../api/personalizationApi';
import './SchoolAutocomplete.css';

export const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

/**
 * 학교 이름 입력 + 나이스 자동완성.
 *
 * - 2자 이상 입력하면 300ms 뒤 `/api/neis/schools` 로 검색해 목록을 보여준다.
 *   학교급 필터 없이 전국 모든 학교를 대상으로 찾는다(학교급은 고른 학교에서 자동으로 채운다).
 * - 목록에서 고르면 `onSelect(school)` 로 표준학교코드가 올라가고, 입력창 아래에
 *   학교 종류·주소만 조용히 보여준다("연결" 같은 기술 용어는 UI에 노출하지 않는다).
 * - 이름을 다시 고치거나 결과가 없으면 코드가 풀리고 입력한 이름 그대로 저장된다(수동 폴백).
 * - 실제 학교 정보(주소·전화 등)는 서버가 나이스에서 채우므로 여기서는 코드만 넘긴다.
 * - 출처 표시(나이스 이용약관 제11조)는 결과 목록 하단에 작은 글씨로만 둔다.
 */
const SchoolAutocomplete = ({
  currentUser,
  value,
  linked,
  disabled,
  onChangeName,
  onSelect,
  onUnlink,
  inputId,
  autoFocus = false,
}) => {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [sampleMode, setSampleMode] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const timerRef = useRef(null);
  const requestSeqRef = useRef(0);
  const rootRef = useRef(null);
  const skipNextSearchRef = useRef(false);

  const isLinked = Boolean(linked?.school_code && linked?.office_code);

  const cancelPending = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    requestSeqRef.current += 1;
  }, []);

  useEffect(() => {
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return undefined;
    }
    const text = String(value || '').trim();
    cancelPending();
    if (isLinked || !currentUser || text.length < MIN_QUERY_LENGTH) {
      setItems([]);
      setSearched(false);
      setSearching(false);
      return undefined;
    }
    const seq = requestSeqRef.current;
    setSearching(true);
    timerRef.current = window.setTimeout(async () => {
      timerRef.current = null;
      try {
        const payload = await searchSchools(currentUser, text);
        if (seq !== requestSeqRef.current) return;
        setItems(Array.isArray(payload?.schools) ? payload.schools : []);
        setSampleMode(Boolean(payload?.sample_mode));
        setSearched(true);
        setOpen(true);
        setActiveIndex(-1);
      } catch (_error) {
        if (seq !== requestSeqRef.current) return;
        // 검색 실패는 조용히 — 입력한 이름 그대로 저장되는 수동 폴백이 있다.
        setItems([]);
        setSearched(true);
      } finally {
        if (seq === requestSeqRef.current) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return cancelPending;
  }, [value, currentUser, isLinked, cancelPending]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const choose = (school) => {
    if (!school) return;
    cancelPending();
    skipNextSearchRef.current = true;
    setOpen(false);
    setItems([]);
    setSearched(false);
    setActiveIndex(-1);
    onSelect(school);
  };

  const handleInput = (event) => {
    const next = event.target.value;
    if (isLinked) onUnlink();
    onChangeName(next);
  };

  const handleKeyDown = (event) => {
    if (!open || items.length === 0) {
      if (event.key === 'Escape') setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % items.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? items.length - 1 : index - 1));
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      choose(items[activeIndex]);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const showList = open && !isLinked && String(value || '').trim().length >= MIN_QUERY_LENGTH;

  return (
    <div className="school-autocomplete" ref={rootRef}>
      <input
        aria-autocomplete="list"
        aria-controls={showList ? listId : undefined}
        aria-expanded={showList}
        autoComplete="off"
        autoFocus={autoFocus}
        disabled={disabled}
        id={inputId}
        maxLength={60}
        onChange={handleInput}
        onFocus={() => {
          if (items.length > 0) setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder="예: 한빛중학교"
        role="combobox"
        value={value}
      />
      {isLinked && (linked.school_kind || linked.school_address) && (
        <p className="school-autocomplete-meta" data-testid="school-autocomplete-meta">
          {[linked.school_kind, linked.school_address].filter(Boolean).join(' · ')}
        </p>
      )}
      {showList && (
        <ul
          aria-label="학교 검색 결과"
          className="school-autocomplete-list"
          id={listId}
          role="listbox"
        >
          {searching && items.length === 0 && (
            <li className="school-autocomplete-hint" role="presentation">학교를 찾고 있어요…</li>
          )}
          {!searching && searched && items.length === 0 && (
            <li className="school-autocomplete-hint" role="presentation">
              찾지 못했어요. 입력한 이름 그대로 저장돼요.
            </li>
          )}
          {items.map((school, index) => (
            <li
              aria-selected={index === activeIndex}
              className={`school-autocomplete-item ${index === activeIndex ? 'is-active' : ''}`}
              key={`${school.office_code}-${school.school_code}`}
              onMouseDown={(event) => {
                event.preventDefault();
                choose(school);
              }}
              onMouseEnter={() => setActiveIndex(index)}
              role="option"
            >
              <strong>{school.school_name}</strong>
              <small>
                {[school.school_kind, school.school_address].filter(Boolean).join(' · ')}
              </small>
            </li>
          ))}
          {items.length > 0 && sampleMode && (
            <li className="school-autocomplete-hint" role="presentation">
              검색 결과가 일부만 보여요. 학교가 없으면 이름을 더 정확히 입력해 주세요.
            </li>
          )}
          {items.length > 0 && (
            <li className="school-autocomplete-source" role="presentation">
              학교 정보 출처: 교육부·시도교육청 나이스 교육정보 개방포털
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default SchoolAutocomplete;
